///
/// This store is for displaying the list of locations
/// ONLY for creating a new observations OR editing an existing one
/// It is NOT used for modifying the list of locations on the
/// datasheet creator, the location details, or the project assigned locations
///

import fetch from "../../../utils/fetch";

const getInitialState = () => {
  return {
    // Status information
    isLoading: false,
    message: null,
    messageType: null,

    // Filter Data
    projectId: null,
    datasheetId: null,

    searchTerm: null,

    // Results
    // A Key value store might be useful for ensuring there are no duplicate locations across searches
    assignments: [],
    total: null,
  };
};
const state = getInitialState();
const getters = {
  // Status
  fetchedCount: (state) => state.assignments.length,
  fetchedPercent: (state, getters) => {
    if (!state.total) return 0;
    return Math.round((getters.fetchedCount / state.total) * 100);
  },
  // Results
  allAssignments: (state) => state.assignments,
  searchFilteredAssignments: (state, getters) => {
    if (!state.searchTerm) return state.assignments;
    return state.assignments.filter((assignment) =>
      assignment.location.name
        .toLowerCase()
        .includes(state.searchTerm.toLowerCase())
    );
  },
  userAssignments: (state, getters) => {
    return getters.searchFilteredAssignments.filter((assignment) => {
      return assignment.member !== null;
    });
  },
  datasheetAssignments: (state, getters) => {
    return getters.searchFilteredAssignments.filter((assignment) => {
      return (
        assignment.member === null && assignment.datasheet === state.datasheetId
      );
    });
  },
  projectAssignments: (state, getters) => {
    return getters.searchFilteredAssignments.filter((assignment) => {
      return assignment.member === null && assignment.datasheet === null;
    });
  },
};

const t = {
  ASSIGNMENTS_RESET: "ASSIGNMENTS_RESET",
  ASSIGNMENTS_START_LOADING: "ASSIGNMENTS_START_LOADING",
  ASSIGNMENTS_ADD: "ASSIGNMENTS_ADD",
  ASSIGNMENTS_FINISH_LOADING: "ASSIGNMENTS_FINISH_LOADING",
  ASSIGNMENTS_SET_MESSAGE: "ASSIGNMENTS_SET_MESSAGE",
  ASSIGNMENTS_SET_SEARCH_TERM: "ASSIGNMENTS_SET_SEARCH_TERM",
};

const mutations = {
  [t.ASSIGNMENTS_RESET](state) {
    Object.assign(state, getInitialState());
  },
  [t.ASSIGNMENTS_START_LOADING](state, { projectId, datasheetId }) {
    state.isLoading = true;
    state.projectId = projectId;
    state.datasheetId = datasheetId;
    state.message = null;
    state.messageType = null;
    state.assignments = [];
    state.total = null;
  },
  [t.ASSIGNMENTS_ADD](state, data) {
    const newAssignedLocations = data["hydra:member"].map((loc, idx) => ({
      ...loc,
      index: idx + 1 + state.assignments.length,
    }));
    // Create a Set of existing @id values. Sometime the API returns duplicates across pages.
    const existingIds = new Set(state.assignments.map((a) => a["@id"]));

    // Filter out any new locations that already exist by @id
    const uniqueNewLocations = newAssignedLocations.filter(
      (loc) => !existingIds.has(loc["@id"])
    );

    // Add only unique locations
    state.assignments = [...state.assignments, ...uniqueNewLocations];
    state.total = data["hydra:totalItems"];
  },
  [t.ASSIGNMENTS_FINISH_LOADING](state) {
    state.isLoading = false;
  },
  [t.ASSIGNMENTS_SET_MESSAGE](state, { message, messageType }) {
    state.message = message;
    state.messageType = messageType;
  },
  [t.ASSIGNMENTS_SET_SEARCH_TERM](state, searchTerm) {
    state.searchTerm = searchTerm;
  },
};

const actions = {
  async fetchAllDatasheetAssignments(
    { commit, dispatch, state },
    { projectId, datasheetId }
  ) {
    if (projectId !== state.projectId || datasheetId !== state.datasheetId) {
      commit(t.ASSIGNMENTS_RESET);
    }
    commit(t.ASSIGNMENTS_START_LOADING, { projectId, datasheetId });
    const itemsPerPage = 100;
    let url = `${state.datasheetId}/assigned_locations_options?itemsPerPage=${itemsPerPage}&order[location.name]=asc&page=1`;

    while (url) {
      const response = await fetch(url);
      if (!response.ok) {
        const error = await response.text();
        commit(t.ASSIGNMENTS_SET_MESSAGE, {
          message: error,
          messageType: "danger",
        });
        break;
      }
      const data = await response.json();
      commit(t.ASSIGNMENTS_ADD, data);
      if (data["hydra:view"] && data["hydra:view"]["hydra:next"]) {
        url = data["hydra:view"]["hydra:next"];
      } else {
        url = null;
        break;
      }
    }
    commit(t.ASSIGNMENTS_FINISH_LOADING);
  },
  async setSearchTerm({ commit }, searchTerm) {
    commit(t.ASSIGNMENTS_SET_SEARCH_TERM, searchTerm);
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
